import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import axiosInstance from "../../utils/axiosConfig";
import ProfileHeader from "./ProfileHeader.jsx";
import AvatarSection from "./AvatarSection.jsx";
import UserInfoSection from "./UserInfoSection.jsx";
import ProfileProgress from "./ProfileProgress.jsx";
import ProfileReset from "./ProfileReset.jsx";
import SocialLinks from "./SocialLinks.jsx";
import LinkAccountModal from "./LinkAccountModal.jsx";
import { fetchCurrentUser, clearUserData } from '../../redux/slices/userSlice';

// Импорт иконок
import telegramIcon from "../../assets/social/telegram.svg";
import instagramIcon from "../../assets/social/instagram.svg";
import siteIcon from "../../assets/social/site.svg";
const Profile = () => {
    // Минимальный набор данных для профиля
    const [userInfo, setUserData] = useState({
        name: "Аноним",
        avatar: "",
        totalLessonScore: 0,
        bonusScore: 0,
        totalScore: 0,
        rank: 0,
        completedLessons: 0,
        totalLessons: 18,
        expireDate: null,
        isAccepted: false
    });

    // Данные для социальных сетей
    const socialLinks = [
        {
            title: "Telegram",
            icon: telegramIcon,
            url: "https://t.me/businessalon"
        },
        {
            title: "Instagram",
            icon: instagramIcon,
            url: "https://www.instagram.com/yana_link?igsh=MWViNnFnc2l1Y2NndA=="
        },
        {
            title: "ВКонтакте",
            icon: siteIcon,
            url: "https://beauty-syndicate.ru/elitlegion"
        }
    ];

    // Используем useCallback для создания мемоизированной функции
    const loadUserProfile = useCallback(() => {
        axiosInstance.post('/profile/get')
            .then((res) => {
                if (res.data.success) {
                    setUserData(res.data.data);
                }
            })
            .catch((error) => console.log("Ошибка загрузки профиля", error));
    }, []);

    // Загрузка данных профиля при монтировании компонента
    useEffect(() => {
        loadUserProfile();
    }, [loadUserProfile]);

    // Функция для обновления данных пользователя
    const updateUserData = (newData) => {
        setUserData(prevData => ({
            ...prevData,
            ...newData
        }));
    };

    const dispatch = useDispatch();
    const accountType = useSelector(state => state.user.accountType);
    const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);

    useEffect(() => {
        if (!accountType) {
            dispatch(fetchCurrentUser());
        }
    }, [dispatch, accountType]);

    const isTelegram = typeof window !== 'undefined'
        && !!(window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.initData);
    const isWeb = !isTelegram;
    const isLinked = accountType === 'linked';
    const canLink = isTelegram && !isLinked;
    console.log(accountType)
    const handleOpenLinkModal = () => {
        if (canLink) {
            setIsLinkModalOpen(true);
        }
    };

    const handleCloseLinkModal = () => {
        setIsLinkModalOpen(false);
    };

    const handleLinkSuccess = async () => {
        setIsLinkModalOpen(false);
        await dispatch(fetchCurrentUser());
        loadUserProfile();
    };

    const handleLogout = async () => {
        try {
            await axiosInstance.post('/auth/logout');
        } catch (err) {
            console.error('Logout error:', err);
        } finally {
            localStorage.removeItem('authToken');
            dispatch(clearUserData());
            window.location.href = '/login';
        }
    };

    const handleCreatePayment = async () => {
        try {
            const response = await axiosInstance.post('/payment/create');
            if (response.data.success && response.data.payment?.confirmation_url) {
                window.open(response.data.payment.confirmation_url, '_blank');
            }
        } catch (error) {
            console.error('Payment creation error:', error);
            alert('Ошибка при создании платежа. Попробуйте позже.');
        }
    };

    const hasActiveSubscription = userInfo.expireDate && new Date(userInfo.expireDate) > new Date();

    return (
        <div className="profile-container">
            <div className="profile-content">
                <div className="profile-card">
                    <AvatarSection
                        userInfo={userInfo}
                        updateUserData={updateUserData}
                    />

                    <div className="avatar-admin-section">
                        <ProfileHeader />
                    </div>

                    <UserInfoSection
                        userInfo={userInfo}
                        updateUserData={updateUserData}
                    />
                </div>

                <ProfileProgress
                    completedLessons={userInfo.completedLessons}
                    totalLessons={userInfo.totalLessons || 18}
                />
                <ProfileReset
                    completedLessons={userInfo.completedLessons}
                    totalLessons={userInfo.totalLessons || 18}
                    onReset={loadUserProfile}
                />

                <SocialLinks links={socialLinks} />

                {isWeb && (
                    <div className="profile-actions">
                        {hasActiveSubscription ? (
                            <div className="subscription-info">
                                <p style={{ fontSize: '14px', color: '#4CAF50', marginBottom: '8px', textAlign: 'center' }}>
                                    ✓ Подписка активна до {new Date(userInfo.expireDate).toLocaleDateString('ru-RU')}
                                </p>
                                <button className="profile-action-button payment-button" onClick={handleCreatePayment}>
                                    Продлить подписку
                                </button>
                            </div>
                        ) : (
                            <div className="subscription-info">
                                <p style={{ fontSize: '14px', color: '#f44336', marginBottom: '8px', textAlign: 'center' }}>
                                    Подписка не активна
                                </p>
                                <button className="profile-action-button payment-button" onClick={handleCreatePayment}>
                                    Оформить подписку
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {isTelegram && canLink && (
                    <div className="profile-actions">
                        <button className="profile-action-button link-web-button" onClick={handleOpenLinkModal}>
                            Связать веб-аккаунт
                        </button>
                        <p className="profile-link-note">
                            Аккаунт можно привязать только один раз
                        </p>
                    </div>
                )}

                {isWeb && (
                    <div className="profile-actions">
                        <button className="profile-action-button logout-button" onClick={handleLogout}>
                            Выйти
                        </button>
                    </div>
                )}

                {isLinkModalOpen && (
                    <LinkAccountModal
                        isOpen={isLinkModalOpen}
                        onClose={handleCloseLinkModal}
                        onSuccess={handleLinkSuccess}
                    />
                )}
            </div>
        </div>
    );
};

export default Profile;
